import { motion, useMotionValue } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import ProfileCard from "./ProfileCard";

const SPEED = 40;
const CARD_WIDTH = 320;
const GAP = 48;
const CARDS_PER_ROW = 5; // define no of cards oer row

const CONTENT_WIDTH = CARD_WIDTH * CARDS_PER_ROW + GAP * (CARDS_PER_ROW - 1);

const AnimatedRow = ({ items, reverse = false }) => {
  const x = useMotionValue(0);

  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [activeCard, setActiveCard] = useState(false);

  const isDragging = useRef(false);
  const lastX = useRef(0);

  const dir = reverse ? 1 : -1;

  /* animate when content width <= screen width */
  useEffect(() => {
    const update = () => {
      const enable = window.innerWidth < CONTENT_WIDTH;
      setShouldAnimate(enable);

      if (!enable) {
        x.set(0);
      }
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [x]);

  useEffect(() => {
    if (!shouldAnimate) return;

    let raf;
    let lastTime = performance.now();

    const loop = (now) => {
      const delta = now - lastTime;
      lastTime = now;

      if (!activeCard && !isDragging.current) {
        let current = x.get();
        current += dir * (delta / 1000) * SPEED;

        if (current <= -CONTENT_WIDTH) current += CONTENT_WIDTH;
        if (current >= 0) current -= CONTENT_WIDTH;

        x.set(current);
      }

      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [shouldAnimate, activeCard, dir, x]);

  const onPointerDown = (e) => {
    if (!shouldAnimate) return;
    lastX.current = e.clientX;
    isDragging.current = false;
  };

  const onPointerMove = (e) => {
    if (!shouldAnimate) return;
    if (e.buttons !== 1 && e.pointerType !== "touch") return;

    const dx = e.clientX - lastX.current;
    if (Math.abs(dx) > 2) isDragging.current = true;
    if (!isDragging.current) return;

    let current = x.get() + dx;
    lastX.current = e.clientX;

    if (current <= -CONTENT_WIDTH) current += CONTENT_WIDTH;
    if (current >= 0) current -= CONTENT_WIDTH;

    x.set(current);
  };

  const onPointerUp = () => {
    isDragging.current = false;
  };

  return (
    <div
      className={`py-16 ${
        shouldAnimate
          ? "overflow-hidden cursor-grab active:cursor-grabbing"
          : "flex justify-center"
      }`}
      style={{ touchAction: shouldAnimate ? "pan-y" : "auto" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
    >
      <motion.div
        className="flex gap-12 w-max select-none"
        style={{ x }}
      >
        {(shouldAnimate ? [...items, ...items] : items).map((item, i) => (
          <ProfileCard
            key={`${item.id}-${i}`}
            specialist={item}
            setActive={setActiveCard}
          />
        ))}
      </motion.div>
    </div>
  );
};

export default AnimatedRow;
